/**
 * Outil « Droite graduée zoomable » (Boîte à outils, 6e → 2nde).
 *
 * Explorer : on place un nombre (décimal, relatif dès la 5e, fraction) et on
 * zoome d'intervalle en intervalle, de l'unité au millième ; à chaque niveau,
 * l'encadrement et l'arrondi. Comparer : deux nombres, zoomer jusqu'à ce
 * qu'ils se séparent. Défi : placer un nombre caché en zoomant soi-même.
 *
 * La droite se manipule au doigt (toucher choisit une graduation et
 * l'intervalle qui la suit) ou au clavier (← →, Entrée pour zoomer, − pour
 * dézoomer).
 */

import {
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { IconButton, Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, INK, MUTED } from '../../ui/svg-parts';
import {
  LEVEL_NAMES,
  MAX_LEVEL,
  UNIT,
  bracket,
  bracketText,
  challengeTarget,
  cmp,
  compareText,
  endOf,
  fmt,
  fromThousandths,
  inWindow,
  numText,
  parseNum,
  placeFeedback,
  separatingLevel,
  shift,
  stepOf,
  ticksOf,
  value,
  windowAround,
  zoomIn,
  zoomOut,
  type Feedback,
  type Num,
  type Win,
} from './line';

const W = 340;
const H = 112;
const X0 = 20;
const X1 = 320;
const Y = 58;
/** couleurs des points A et B (classes : contrastées en thème clair et sombre) */
const MARKER_CLASSES = ['fill-primary-600', 'fill-[#c2410c] ocean:fill-[#fb923c]'];
const NNBSP = String.fromCharCode(0x202f);
const KEYS_HELP =
  'Flèches gauche et droite : choisir une graduation ; Début, Fin : aux extrémités ; Entrée : zoomer ; moins : dézoomer.';

const STEP_TEXT = ['1', '0,1', '0,01', '0,001'];

interface Marker {
  name: string;
  x: Num;
  cls: string;
}

type Placed = { t: number; state: 'ok' | 'near' | 'bad' };

/** position d'une graduation (millièmes) dans la figure */
const xOf = (w: Win, t: number) => X0 + ((t - w.start) / (10 * stepOf(w.level))) * (X1 - X0);

function LineFigure({
  win,
  cur,
  sel,
  markers,
  placed,
  onPick,
  onKey,
  half,
  svgRef,
}: {
  win: Win;
  cur: number;
  sel: number;
  markers: readonly Marker[];
  /** graduation posée au défi (juste, presque, fausse) */
  placed: Placed | null;
  onPick: (cur: number, sel: number) => void;
  onKey: (e: KeyboardEvent<SVGSVGElement>) => void;
  /** 6e : demi-droite graduée (pas de nombres négatifs) */
  half: boolean;
  svgRef?: RefObject<SVGSVGElement | null>;
}) {
  const own = useRef<SVGSVGElement>(null);
  const ref = svgRef ?? own;
  const helpId = useId();
  const ticks = ticksOf(win);
  const st = stepOf(win.level);
  const labels = ticks.map(fmt);
  const long = labels.some((l) => l.length > 4);
  const pick = (e: PointerEvent<SVGSVGElement>) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    const px = ((e.clientX - r.left) / r.width) * W;
    const f = ((px - X0) / (X1 - X0)) * 10;
    const c = Math.max(0, Math.min(10, Math.round(f)));
    const s = Math.max(0, Math.min(9, Math.floor(f)));
    onPick(c, s);
  };
  const curT = ticks[cur]!;
  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${W} ${H}`}
      role="slider"
      tabIndex={0}
      aria-label={half ? 'Demi-droite graduée' : 'Droite graduée'}
      aria-describedby={helpId}
      aria-valuemin={win.start / UNIT}
      aria-valuemax={endOf(win) / UNIT}
      aria-valuenow={curT / UNIT}
      aria-valuetext={`graduation ${fmt(curT)}, pas de ${STEP_TEXT[win.level]}`}
      onPointerDown={pick}
      onKeyDown={onKey}
      className={cn(
        'border-ink-100 focus-visible:ring-primary-500 w-full max-w-[420px] touch-manipulation rounded-2xl border outline-none focus-visible:ring-2',
        FIGURE_BG_CLASS,
      )}
    >
      {/* intervalle choisi, avec ses 10 sous-graduations (ce qu'on verra en zoomant) */}
      <rect
        x={xOf(win, ticks[sel]!)}
        y={Y - 12}
        width={(X1 - X0) / 10}
        height={24}
        fill="var(--color-primary-500)"
        fillOpacity={0.14}
        rx={3}
      />
      {win.level < MAX_LEVEL &&
        Array.from({ length: 9 }, (_, k) => {
          const x = xOf(win, ticks[sel]! + ((k + 1) * st) / 10);
          return (
            <line key={k} x1={x} x2={x} y1={Y - 4} y2={Y + 4} stroke={MUTED} strokeWidth={1} />
          );
        })}
      <desc id={helpId}>{KEYS_HELP}</desc>
      {/* une demi-droite part de son origine 0 */}
      <line
        x1={half && win.start === 0 ? X0 : 8}
        x2={W - 8}
        y1={Y}
        y2={Y}
        stroke={INK}
        strokeWidth={1.6}
      />
      <path d={`M${W - 8},${Y} l-7,-4 v8 z`} fill={INK} />
      {ticks.map((t, i) => {
        const x = xOf(win, t);
        const show = !long || i % 2 === 0;
        return (
          <g key={t}>
            <line x1={x} x2={x} y1={Y - 9} y2={Y + 9} stroke={INK} strokeWidth={1.4} />
            {show && (
              <text
                x={x}
                y={Y + 26}
                fontSize={12}
                textAnchor="middle"
                fill={INK}
                fontWeight={i === 0 || i === 10 ? 700 : 400}
              >
                {labels[i]}
              </text>
            )}
          </g>
        );
      })}
      {/* curseur */}
      <path
        d={`M${xOf(win, curT)},${Y + 30} l-6,9 h12 z`}
        fill="var(--color-primary-500)"
        aria-hidden
      />
      {placed && (
        <circle
          cx={xOf(win, placed.t)}
          cy={Y}
          r={5.5}
          fill={
            placed.state === 'ok'
              ? 'var(--color-success-500)'
              : placed.state === 'near'
                ? 'var(--color-warning-500)'
                : 'var(--color-error-500)'
          }
          stroke="#fff"
          strokeWidth={1.5}
        />
      )}
      {markers.map((m, k) => {
        if (inWindow(win, m.x)) {
          const x = X0 + ((value(m.x) * UNIT - win.start) / (10 * st)) * (X1 - X0);
          return (
            <g key={m.name}>
              <circle cx={x} cy={Y} r={5.5} className={m.cls} stroke="#fff" strokeWidth={1.5} />
              <HaloText
                x={x}
                y={Y - 16 - k * 14}
                fontSize={13}
                fontWeight={700}
                textAnchor="middle"
                className={m.cls}
              >
                {m.name}
              </HaloText>
            </g>
          );
        }
        const left = cmp(m.x, fromThousandths(win.start)) < 0;
        return (
          <HaloText
            key={m.name}
            x={left ? 10 : W - 10}
            y={Y - 16 - k * 14}
            fontSize={13}
            fontWeight={700}
            textAnchor={left ? 'start' : 'end'}
            className={m.cls}
          >
            {left ? `← ${m.name}` : `${m.name} →`}
          </HaloText>
        );
      })}
    </svg>
  );
}

/** fenêtre + curseur + boutons de navigation, partagés par les trois modes */
function useLine(negative: boolean, initial: Win = { level: 0, start: 0 }) {
  const [win, setWinRaw] = useState<Win>(initial);
  const [cur, setCur] = useState(5);
  const [sel, setSel] = useState(5);
  const setWin = (w: Win) => {
    setWinRaw(w);
    setCur(5);
    setSel(5);
  };
  const zoom = () => setWin(zoomIn(win, sel));
  const unzoom = () => setWin(zoomOut(win));
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'End') {
      const c =
        e.key === 'Home'
          ? 0
          : e.key === 'End'
            ? 10
            : Math.max(0, Math.min(10, cur + (e.key === 'ArrowRight' ? 1 : -1)));
      setCur(c);
      setSel(Math.min(9, c));
    } else if (e.key === 'Enter' || e.key === '+') zoom();
    else if (e.key === '-' || e.key === '−') unzoom();
    else return;
    e.preventDefault();
  };
  return {
    win,
    cur,
    sel,
    setWin,
    zoom,
    unzoom,
    onKey,
    onPick: (c: number, s: number) => {
      setCur(c);
      setSel(s);
    },
    shiftBy: (d: -1 | 1) => setWin(shift(win, d, negative)),
    canShift: (d: -1 | 1) => shift(win, d, negative) !== win,
    select: (i: number) => {
      setCur(i);
      setSel(Math.min(9, i));
    },
  };
}

function NavButtons({ line }: { line: ReturnType<typeof useLine> }) {
  const t = ticksOf(line.win);
  const a = t[line.sel]!;
  const b = t[line.sel + 1]!;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <IconButton
        label="Fenêtre précédente (vers la gauche)"
        onClick={() => line.shiftBy(-1)}
        disabled={!line.canShift(-1)}
      >
        <ChevronLeft className="h-5 w-5" aria-hidden />
      </IconButton>
      <IconButton
        label="Fenêtre suivante (vers la droite)"
        onClick={() => line.shiftBy(1)}
        disabled={!line.canShift(1)}
      >
        <ChevronRight className="h-5 w-5" aria-hidden />
      </IconButton>
      <IconButton label="Dézoomer" onClick={line.unzoom} disabled={line.win.level === 0}>
        <ZoomOut className="h-5 w-5" aria-hidden />
      </IconButton>
      <button
        type="button"
        className={cn(SECONDARY_BUTTON, 'inline-flex items-center gap-1.5')}
        onClick={line.zoom}
        disabled={line.win.level >= MAX_LEVEL}
      >
        <ZoomIn className="h-4 w-4" aria-hidden />
        {`Zoomer entre ${fmt(a)} et ${fmt(b)}`}
      </button>
    </div>
  );
}

function StepInfo({ win }: { win: Win }) {
  return (
    <p className="text-ink-600 text-xs">
      {frenchSpaces(
        `Entre deux graduations : ${STEP_TEXT[win.level]}${win.level < MAX_LEVEL ? ' (touche un intervalle, puis zoome)' : ' : c’est le plus fin'}.`,
      )}
    </p>
  );
}

function NumberInput({
  label,
  value: text,
  onChange,
  error,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error: boolean;
  hint: string;
}) {
  const errId = useId();
  return (
    <div>
      <label className="block">
        <span className="text-ink-700 text-sm font-semibold">{label}</span>
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error}
          aria-describedby={error ? errId : undefined}
          className={cn(
            'border-ink-200 text-ink-900 font-display mt-1 block min-h-11 w-40 rounded-xl border bg-transparent px-3 text-lg font-bold tabular-nums',
            error && 'border-error-600',
          )}
        />
      </label>
      {error && (
        <span id={errId} className="text-error-600 mt-1 block max-w-xs text-xs">
          {hint}
        </span>
      )}
    </div>
  );
}

const hintText = (negative: boolean) =>
  negative
    ? frenchSpaces(
        `Écris un décimal (−3,47 ; 12,5), au plus 3 décimales, ou une fraction (7/3), entre −1${NNBSP}000 et 1${NNBSP}000.`,
      )
    : `Écris un décimal positif (3,47), au plus 3 décimales, ou une fraction (7/3), jusqu’à 1${NNBSP}000.`;

/* ---------- Explorer ---------- */

function Explore({ negative }: { negative: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [text, setText] = useState('3,47');
  const parsed = parseNum(text, { negative });
  const [x, setX] = useState<Num>(parsed ?? fromThousandths(3470));
  const line = useLine(negative);
  const onText = (v: string) => {
    setText(v);
    const p = parseNum(v, { negative });
    if (p) setX(p);
  };
  const levels = [];
  for (let k = 0; k <= MAX_LEVEL; k++) {
    const b = bracket(x, k);
    levels.push(b);
    if (b.exact) break;
  }
  return (
    <div className="space-y-3">
      <NumberInput
        label="Nombre à placer (A)"
        value={text}
        onChange={onText}
        error={!parsed}
        hint={hintText(negative)}
      />
      <LineFigure
        win={line.win}
        cur={line.cur}
        sel={line.sel}
        markers={[{ name: 'A', x, cls: MARKER_CLASSES[0]! }]}
        placed={null}
        onPick={line.onPick}
        onKey={line.onKey}
        half={!negative}
        svgRef={svgRef}
      />
      <StepInfo win={line.win} />
      <NavButtons line={line} />
      {!inWindow(line.win, x) && (
        <button
          type="button"
          className={PRIMARY_BUTTON}
          onClick={() => {
            line.setWin(windowAround(line.win.level, x));
            // le bouton disparaît : le focus passe à la droite
            svgRef.current?.focus();
          }}
        >
          {frenchSpaces(`Aller à A (${numText(x)})`)}
        </button>
      )}
      <div className="bg-ink-50 rounded-2xl px-3 py-2 text-sm">
        <p className="text-ink-900 font-semibold">{frenchSpaces(`A = ${numText(x)}`)}</p>
        <ul className="mt-1 space-y-1">
          {levels.map((b) => (
            <li
              key={b.level}
              className={cn(
                'text-ink-700',
                b.level === line.win.level && 'text-ink-900 font-semibold',
              )}
            >
              {frenchSpaces(
                b.exact
                  ? `${LEVEL_NAMES[b.level]} : ${numText(x)} tombe sur une graduation.`
                  : `${LEVEL_NAMES[b.level]} : ${bracketText(x, b)} ; arrondi ${LEVEL_NAMES[b.level]} : ${fmt(b.round)}.`,
              )}
            </li>
          ))}
          {!levels.at(-1)!.exact && (
            <li className="text-ink-600 text-xs">
              {frenchSpaces(`${numText(x)} ne tombe sur aucune graduation, même au millième.`)}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* ---------- Comparer ---------- */

function Compare({ negative }: { negative: boolean }) {
  const [ta, setTa] = useState('2,5');
  const [tb, setTb] = useState('2,45');
  const pa = parseNum(ta, { negative });
  const pb = parseNum(tb, { negative });
  const [a, setA] = useState<Num>(pa ?? fromThousandths(2500));
  const [b, setB] = useState<Num>(pb ?? fromThousandths(2450));
  const line = useLine(negative);
  const sep = separatingLevel(a, b);
  const separate = () => {
    if (sep === null) return;
    line.setWin(windowAround(sep, cmp(a, b) <= 0 ? a : b));
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <NumberInput
          label="Nombre A"
          value={ta}
          onChange={(v) => {
            setTa(v);
            const p = parseNum(v, { negative });
            if (p) setA(p);
          }}
          error={!pa}
          hint={hintText(negative)}
        />
        <NumberInput
          label="Nombre B"
          value={tb}
          onChange={(v) => {
            setTb(v);
            const p = parseNum(v, { negative });
            if (p) setB(p);
          }}
          error={!pb}
          hint={hintText(negative)}
        />
      </div>
      <LineFigure
        win={line.win}
        cur={line.cur}
        sel={line.sel}
        markers={[
          { name: 'A', x: a, cls: MARKER_CLASSES[0]! },
          { name: 'B', x: b, cls: MARKER_CLASSES[1]! },
        ]}
        placed={null}
        onPick={line.onPick}
        onKey={line.onKey}
        half={!negative}
      />
      <StepInfo win={line.win} />
      <NavButtons line={line} />
      {sep !== null && sep > 0 && (
        <button type="button" className={PRIMARY_BUTTON} onClick={separate}>
          Zoomer jusqu’à les séparer
        </button>
      )}
      <Note live>{frenchSpaces(compareText(a, b))}</Note>
    </div>
  );
}

/* ---------- Défi ---------- */

function Challenge({ negative, rank }: { negative: boolean; rank: number }) {
  const [target, setTarget] = useState<Num>(() => challengeTarget(rank, Math.random));
  const [fb, setFb] = useState<Feedback | null>(null);
  const [placed, setPlaced] = useState<Placed | null>(null);
  const [tries, setTries] = useState(0);
  const line = useLine(negative);
  const place = () => {
    const t = ticksOf(line.win)[line.cur]!;
    const f = placeFeedback(target, t, line.win.level);
    setFb(f);
    setPlaced({ t, state: f.kind === 'ok' ? 'ok' : f.kind === 'zoom' ? 'near' : 'bad' });
    setTries(tries + 1);
    // « presque » : l'intervalle qui contient le nombre est choisi, prêt à zoomer
    if (f.kind === 'zoom') {
      const i = ticksOf(line.win).indexOf(f.lo);
      if (i >= 0 && i <= 9) line.select(i);
    }
  };
  const next = () => {
    setTarget(challengeTarget(rank, Math.random));
    setFb(null);
    setPlaced(null);
    setTries(0);
    line.setWin({ level: 0, start: 0 });
  };
  const ts = numText(target);
  const msg =
    fb === null
      ? null
      : fb.kind === 'ok'
        ? `Bravo : ${ts} est bien là${tries > 1 ? ` (en ${tries} essais)` : ''} !`
        : fb.kind === 'zoom'
          ? `Presque : ${ts} est entre ${fmt(fb.lo)} et ${fmt(fb.hi)}. Zoome sur cet intervalle pour le placer exactement.`
          : `Tu as placé ${fmt(fb.placed)} : ${ts} est plus ${fb.kind === 'right' ? 'à droite' : 'à gauche'}${
              fb.kind === 'right' ? ` (${fmt(fb.placed)} < ${ts})` : ` (${ts} < ${fmt(fb.placed)})`
            }.`;
  return (
    <div className="space-y-3">
      <p className="text-ink-900 text-base font-semibold">
        {frenchSpaces(`Place ${ts} sur la ${negative ? 'droite' : 'demi-droite'} graduée.`)}
      </p>
      <LineFigure
        win={line.win}
        cur={line.cur}
        sel={line.sel}
        markers={[]}
        placed={placed && placed.t >= line.win.start && placed.t <= endOf(line.win) ? placed : null}
        onPick={line.onPick}
        onKey={line.onKey}
        half={!negative}
      />
      <StepInfo win={line.win} />
      <NavButtons line={line} />
      <div className="flex flex-wrap gap-2">
        <button type="button" className={PRIMARY_BUTTON} onClick={place}>
          {frenchSpaces(`Placer sur ${fmt(ticksOf(line.win)[line.cur]!)}`)}
        </button>
        <button type="button" className={SECONDARY_BUTTON} onClick={next}>
          Nouveau nombre
        </button>
      </div>
      <div aria-live="polite">
        {msg && (
          <Note live={false} tone={fb?.kind === 'ok' ? 'success' : 'neutral'}>
            {frenchSpaces(msg)}
          </Note>
        )}
      </div>
    </div>
  );
}

export function NumberLineTool({ level }: { level?: string | null }) {
  const rank = Math.min(5, Math.max(1, schoolLevelRank(level) ?? 2));
  const negative = rank >= 2;
  const [mode, setMode] = useState<'explore' | 'compare' | 'challenge'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'compare', label: 'Comparer' },
          { value: 'challenge', label: frenchSpaces('Défi : placer') },
        ]}
        onChange={setMode}
      />
      {mode === 'explore' && <Explore negative={negative} />}
      {mode === 'compare' && <Compare negative={negative} />}
      {mode === 'challenge' && <Challenge negative={negative} rank={rank} />}
    </div>
  );
}
